import ClearIcon from "@/assets/images/icons/clear-icon.svg";
import SearchIcon from "@/assets/images/icons/search-icon.svg";
import {
  CollectionFallbackTitle,
  CollectionHeaderSpacer,
  CollectionPageHeader,
} from "@/components/course/CollectionPageHeader";
import { CoursePagePremiumLoader } from "@/components/course/CoursePagePremiumLoader";
import { LessonPreview } from "@/components/course/LessonPreview";
import { HelpScoutBeacon } from "@/components/HelpScoutBeacon";
import { LessonsGridList } from "@/components/lesson/LessonsGridList";
import { ContentWrapper, ErrorDisplay, useErrorHandler } from "@/components/ui";
import { DashboardViewModeToggle } from "@/components/ui/DashboardViewModeToggle";
import { DashboardToolbarDropdown } from "@/components/ui/DashboardToolbarDropdown";
import { DashboardOutlinedIconButton } from "@/components/ui/DashboardOutlinedIconButton";
import {
  DESKTOP_SEARCH_MAX_WIDTH,
  SearchInputField,
} from "@/components/ui/SearchInputField";
import { useCollectionPageData } from "@/hooks/useCollectionQueries";
import { useHideLessonFromContinue } from "@/hooks/useHideLessonFromContinue";
import { useSearchTracking } from "@/hooks/useSearchTracking";
import { formatPageTitle, useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  effectiveMinStatusForCourse,
  useDraftViewer,
} from "@/hooks/useDraftViewer";
import { useCourse } from "@/hooks/useCourse";
import { useDefaultLessonViewMode } from "@/hooks/useDefaultLessonViewMode";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useAuth } from "@/lib/AuthContext";
import { COLLECTION_SLUGS, LessonStatus } from "@/lib/constants";
import { lessonSearchPlaceholder } from "@/lib/courseSearchPlaceholder";
import {
  DASHBOARD_TOOLBAR_CONTROL_HEIGHT,
  DASHBOARD_TOOLBAR_DROPDOWN_WIDTH_SORT,
  DASHBOARD_TOOLBAR_ICON_INACTIVE,
} from "@/lib/dashboardToolbarUi";
import { logger } from "@/lib/logger";
import { useCurrentUrl } from "@/lib/useCurrentPath";
import {
  CONTENT_MAX_WIDTH,
  useIsMobile,
  useLessonCardLayout,
} from "@/lib/useDeviceType";
import { mixpanelService } from "@/services/mixpanel.service";
import { type SortOrder } from "@/supabase/services/lesson";
import { Lesson } from "@/types";
import { buildLessonUrl } from "@/utils/buildLessonUrl";
import { shouldSkipPreview as shouldSkipPreviewUtil } from "@/utils/lessonHelpers";
import { useNavigation } from "@react-navigation/native";
import { useLocalSearchParams, usePathname, useRouter } from "expo-router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView } from "react-native";
import { XStack, YStack } from "tamagui";
import { EyebrowText } from "@/components/ui/Typography";

function getSortOrderLabel(order: SortOrder): string {
  return order === "by_title" ? "A-Z" : "Newest to Oldest";
}

export default function CollectionDetailScreen() {
  const {
    "course-slug": courseSlug,
    "collection-slug": collectionSlug,
  } = useLocalSearchParams<{
    "course-slug": string;
    "collection-slug": string;
    id?: string;
    courseId?: string;
  }>();

  // Visibility is derived from the draft_viewers allowlist, not a URL param.
  // A course flagged hide_from_draft_viewers clamps back to Published-only,
  // so we wait for the course row before running the lesson queries.
  const canViewDrafts = useDraftViewer();
  const collectionCourseQuery = useCourse(courseSlug);
  const minStatus = effectiveMinStatusForCourse(
    canViewDrafts ? LessonStatus.Retired : LessonStatus.Published,
    collectionCourseQuery.data?.metadata,
  );

  const router = useRouter();
  const navigation = useNavigation();
  const isMobile = useIsMobile();
  const lessonCardLayout = useLessonCardLayout();
  const currentPath = usePathname();
  const { fullPath } = useCurrentUrl();
  const { user } = useAuth();
  const { showError, showSuccess } = useErrorHandler();

  // Persisted state - automatically synced with storage (view mode per category)
  const [sortOrder, setSortOrder, sortOrderLoaded] =
    usePersistedState<SortOrder>("collection_sort_order", "by_date");
  const viewModeKey =
    courseSlug && collectionSlug
      ? `collection_view_mode_${courseSlug}_${collectionSlug}`
      : "collection_view_mode";
  const [viewMode, setViewMode, viewModeLoaded, viewModeHadStored] =
    usePersistedState<"grid" | "list">(viewModeKey, "list");
  // Fetch collection/section page data with Tanstack Query
  const { collection, lessons, userLessonsMap, isLoading, error, queries } =
    useCollectionPageData(
      courseSlug,
      collectionSlug,
      sortOrder,
      minStatus,
      user?.id,
      sortOrderLoaded && collectionCourseQuery.data !== undefined,
    );

  useDocumentTitle(
    collection?.title
      ? formatPageTitle(collection.title, collection.courseTitle)
      : null,
  );

  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [lesson, setLesson] = useState<Lesson | null>(null);

  const shouldSkipPreview = useCallback(
    (l: Lesson) =>
      shouldSkipPreviewUtil(l, userLessonsMap, {
        viewMode,
        includeSummaryVisible: true,
      }),
    [userLessonsMap, viewMode],
  );

  const handleLessonPress = useCallback(
    (l: Lesson) => {
      if (collectionSlug === COLLECTION_SLUGS.CONTINUE && l.course_slug) {
        router.push(
          buildLessonUrl({
            courseSlug: l.course_slug,
            lessonSlug: l.slug,
            referrer: `/courses/${l.course_slug}`,
            referrerTitle: l.course_title,
            courseTitle: l.course_title ?? collection?.courseTitle,
          }) as any,
        );
        return;
      }
      if (shouldSkipPreview(l)) {
        router.push(
          buildLessonUrl({
            courseSlug: String(courseSlug),
            lessonSlug: l.slug,
            referrer: `/courses/${courseSlug}/collections/${collectionSlug}`,
            referrerTitle: collection?.title,
            courseTitle: collection?.courseTitle,
          }) as any,
        );
        return;
      }
      setLesson(l);
      if (isMobile) {
        setSheetOpen(true);
        setModalOpen(false);
      } else {
        setModalOpen(true);
        setSheetOpen(false);
      }
    },
    [shouldSkipPreview, collectionSlug, router, courseSlug, isMobile, collection?.title, collection?.courseTitle],
  );

  const handlePlayPress = useCallback(() => {
    setModalOpen(false);
    setSheetOpen(false);
  }, []);

  const isContinueCollection = collectionSlug === COLLECTION_SLUGS.CONTINUE;
  const hideLessonFromContinue = useHideLessonFromContinue(user?.id);
  const handleRemoveLesson = useCallback(
    (l: Lesson) => {
      hideLessonFromContinue.mutate(l.id, {
        onSuccess: () => showSuccess("Removed from Continue watching"),
        onError: (err) =>
          showError(
            err instanceof Error ? err.message : "Failed to remove lesson",
          ),
      });
    },
    [hideLessonFromContinue, showSuccess, showError],
  );

  useEffect(() => {
    if (Platform.OS !== "web" && collection?.courseTitle) {
      navigation.setOptions({
        title: collection.courseTitle,
      });
    }
  }, [collection?.courseTitle, navigation]);

  // Default view by lesson count when no preference stored: ≤12 list, >12 grid
  useDefaultLessonViewMode(
    viewModeLoaded,
    viewModeHadStored,
    lessons.length,
    setViewMode,
  );

  const allowLessonSearch = collection?.allow_lesson_search === true;
  const searchPlaceholder = lessonSearchPlaceholder(
    collection?.lesson_search_placeholder,
  );

  // Client-side search filtering
  const filteredLessons = useMemo(() => {
    if (!allowLessonSearch) return lessons;
    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (!trimmedQuery) return lessons;

    return lessons.filter((lesson) =>
      lesson.title.toLowerCase().includes(trimmedQuery),
    );
  }, [lessons, searchQuery, allowLessonSearch]);

  const searchTracking = useSearchTracking({
    scope: "in_course",
    courseId: collectionCourseQuery.data?.id != null
      ? Number(collectionCourseQuery.data.id)
      : null,
    courseSlug: String(courseSlug),
    courseTitle: collection?.courseTitle,
  });

  const isLessonSearchActive =
    allowLessonSearch && searchQuery.trim().length >= 2;

  useEffect(() => {
    if (!isLessonSearchActive) return;
    searchTracking.onResultsSettled(
      searchQuery.trim(),
      filteredLessons.map((l) => ({
        id: l.id,
        type: "lesson" as const,
        slug: l.slug,
        title: l.title,
        courseSlug: l.course_slug ?? String(courseSlug),
      })),
    );
  }, [
    isLessonSearchActive,
    searchQuery,
    filteredLessons,
    searchTracking,
    courseSlug,
  ]);

  const trackedLessonPress = useCallback(
    (l: Lesson) => {
      if (isLessonSearchActive) {
        const position = filteredLessons.findIndex((r) => r.id === l.id) + 1;
        searchTracking.trackResultClick(
          searchQuery.trim(),
          {
            id: l.id,
            type: "lesson",
            slug: l.slug,
            title: l.title,
            courseSlug: l.course_slug ?? String(courseSlug),
          },
          position,
        );
      }
      handleLessonPress(l);
    },
    [
      isLessonSearchActive,
      filteredLessons,
      searchQuery,
      searchTracking,
      courseSlug,
      handleLessonPress,
    ],
  );

  const handleSearchChange = useCallback((text: string) => {
    setSearchQuery(text);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchQuery("");
    if (isMobile) setIsSearchActive(false);
  }, [isMobile]);

  // Refetch all queries (for error retry)
  const refetchAll = useCallback(async () => {
    await Promise.all([
      queries.collectionQuery.refetch(),
      queries.lessonsQuery.refetch(),
      queries.userProgressQuery.refetch(),
    ]);
  }, [queries]);

  const handleSortOrderChange = useCallback(
    async (newSortOrder: SortOrder) => {
      if (sortOrder === newSortOrder) return;
      setIsSortMenuOpen(false);
      await setSortOrder(newSortOrder);
    },
    [sortOrder, setSortOrder],
  );

  const handleViewModeChange = useCallback(
    async (newViewMode: "grid" | "list") => {
      if (viewMode === newViewMode) return;
      await setViewMode(newViewMode);
    },
    [viewMode, setViewMode],
  );

  // Log errors with currentPath (route) for Sentry
  useEffect(() => {
    if (error) {
      logger.error("Failed to load collection/section page data:", {
        courseSlug,
        collectionSlug,
        userId: user?.id,
        sortOrder,
        route: currentPath,
        error,
      });
    }
  }, [error, courseSlug, collectionSlug, user?.id, sortOrder, currentPath]);

  // Track collection/section page visit once, after data settles so the event
  // carries human-readable titles (slug-only if the load failed).
  const collectionVisitTracked = useRef(false);
  useEffect(() => {
    if (!courseSlug || !collectionSlug || collectionVisitTracked.current) return;
    if (isLoading) return;
    collectionVisitTracked.current = true;
    mixpanelService.trackCollectionsVisit(
      String(courseSlug),
      String(collectionSlug),
      user,
      fullPath,
      collection?.courseTitle,
      collection?.title,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSlug, collectionSlug, isLoading, collection?.title, user?.id]);

  if (isLoading || !sortOrderLoaded || !viewModeLoaded) {
    return (
      <YStack f={1} bg="$bg">
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: "center",
            paddingBottom: Platform.OS === "web" ? 0 : 16,
          }}
          showsVerticalScrollIndicator={Platform.OS === "web"}
        >
          <CoursePagePremiumLoader variant="lessons" />
        </ScrollView>
      </YStack>
    );
  }

  if (error) {
    return (
      <YStack f={1} bg="$bg">
        <ErrorDisplay
          error={error}
          onRetry={refetchAll}
          onDismiss={() => {
            if (router.canGoBack?.()) {
              router.back();
            } else if (courseSlug) {
              router.replace({
                pathname: "/courses/[course-slug]",
                params: { "course-slug": String(courseSlug) },
              });
            } else {
              router.replace("/courses");
            }
          }}
        />
      </YStack>
    );
  }

  return (
    <YStack f={1} bg="$bg" position="relative">
      <HelpScoutBeacon />
      <LessonsGridList
        lessons={filteredLessons}
        viewMode={viewMode}
        isMobile={isMobile}
        searchQuery={allowLessonSearch ? searchQuery : ""}
        userLessonsMap={userLessonsMap}
        onPressLesson={trackedLessonPress}
        onRemoveLesson={
          isContinueCollection && user ? handleRemoveLesson : undefined
        }
        lessonCardLayout={lessonCardLayout}
        showCourseColor={collectionSlug === COLLECTION_SLUGS.CONTINUE}
        ListHeaderComponent={
          <ContentWrapper py={0}>
            <CollectionPageHeader
              courseSlug={String(courseSlug)}
              collection={collection}
              isMobile={isMobile}
            />

            <YStack px={12} py={16}>
              <YStack w="100%" ai="center">
                <XStack
                  w="100%"
                  maxWidth={CONTENT_MAX_WIDTH}
                  ai="center"
                  jc="space-between"
                  gap="$2"
                  fw="wrap"
                  minHeight={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
                  $gtSm={{ gap: "$3", fw: "nowrap" }}
                >
                  <XStack
                    ai="center"
                    gap="$2"
                    h={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
                    minHeight={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
                    flex={
                      allowLessonSearch && isMobile && isSearchActive ? 1 : 0
                    }
                    flexShrink={1}
                    $gtSm={{ flex: 1, gap: "$3" }}
                  >
                    {allowLessonSearch && isMobile && !isSearchActive && (
                      <DashboardOutlinedIconButton
                        onPress={() => setIsSearchActive(true)}
                        accessibilityLabel="Search lessons"
                      >
                        <SearchIcon
                          width={16}
                          height={16}
                          color={DASHBOARD_TOOLBAR_ICON_INACTIVE}
                        />
                      </DashboardOutlinedIconButton>
                    )}

                    {allowLessonSearch && (isSearchActive || !isMobile) && (
                      <SearchInputField
                        flex={isMobile ? 1 : undefined}
                        isMobile={isMobile}
                        value={searchQuery}
                        onChangeText={handleSearchChange}
                        placeholder={searchPlaceholder}
                        onFocus={() => isMobile && setIsSearchActive(true)}
                        width={isMobile ? "100%" : undefined}
                        containerProps={
                          !isMobile
                            ? { maxWidth: DESKTOP_SEARCH_MAX_WIDTH }
                            : undefined
                        }
                        trailing={
                          (isMobile && isSearchActive) ||
                          (!isMobile && searchQuery.length > 0) ? (
                            <Pressable
                              onPress={handleSearchClear}
                              hitSlop={8}
                              style={{
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <ClearIcon width={16} />
                            </Pressable>
                          ) : null
                        }
                      />
                    )}

                    {(!isSearchActive || !isMobile) &&
                      collection?.sort_orders_enabled && (
                        <DashboardToolbarDropdown
                          label={getSortOrderLabel(sortOrder)}
                          open={isSortMenuOpen}
                          onOpenChange={setIsSortMenuOpen}
                          width={DASHBOARD_TOOLBAR_DROPDOWN_WIDTH_SORT}
                          content={
                            <YStack gap="$3" p="$3.5">
                              <Pressable
                                onPress={() =>
                                  handleSortOrderChange("by_title")
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <EyebrowText
                                  color={
                                    sortOrder === "by_title"
                                      ? "#0D0D0D"
                                      : "#5C5854"
                                  }
                                >
                                  A-Z
                                </EyebrowText>
                              </Pressable>
                              <Pressable
                                onPress={() => handleSortOrderChange("by_date")}
                                style={{ cursor: "pointer" }}
                              >
                                <EyebrowText
                                  color={
                                    sortOrder === "by_date"
                                      ? "#0D0D0D"
                                      : "#5C5854"
                                  }
                                >
                                  Newest to Oldest
                                </EyebrowText>
                              </Pressable>
                            </YStack>
                          }
                        />
                      )}
                  </XStack>

                  {(!allowLessonSearch || !isSearchActive) && (
                    <DashboardViewModeToggle
                      viewMode={viewMode}
                      onViewModeChange={handleViewModeChange}
                      flexShrink={0}
                      ml="auto"
                      h={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
                      minHeight={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
                      $gtSm={{ ml: 0, gap: "$2" }}
                    />
                  )}
                </XStack>
              </YStack>
            </YStack>

            {!(collection && collection.id > 0) ? (
              <CollectionFallbackTitle title={collection?.title} />
            ) : null}

            <CollectionHeaderSpacer
              hasCollectionHero={!!(collection && collection.id > 0)}
            />
          </ContentWrapper>
        }
      />

      <LessonPreview
        lesson={lesson}
        sheetOpen={sheetOpen}
        modalOpen={modalOpen}
        onSheetChange={setSheetOpen}
        onModalChange={setModalOpen}
        onPlayPress={handlePlayPress}
        userLessonsMap={userLessonsMap}
        courseSlug={lesson?.course_slug ?? String(courseSlug)}
        userId={user?.id}
        showError={showError}
        showSuccess={showSuccess}
        collectionId={collection?.id}
        referrer={
          lesson?.course_slug
            ? `/courses/${lesson.course_slug}`
            : `/courses/${courseSlug}/collections/${collectionSlug}`
        }
        referrerTitle={lesson?.course_title ?? collection?.title}
        courseTitle={lesson?.course_title ?? collection?.courseTitle ?? undefined}
      />
    </YStack>
  );
}
